'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getHeroHeaderSizeClasses } from '@/utils/getHeroHeaderSizeClasses';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getHeroVerticalSpacing } from '@/utils/getVerticalSpacing';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Helpers ------------------------------ */

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && x && 'value' in x) return x.value ?? fallback;
    return x;
}
const trim = (s) => String(s ?? '').trim();

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        ['ctas', 'ctas-only', 'cta', 'cta-only', 'buttons', 'button'].includes(
            v
        )
    ) {
        return 'ctas-only';
    }
    return 'text-content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

/** Alignment helpers (match Hero/USP) */
function getAlignmentClasses(alignment) {
    switch (String(alignment || 'center').toLowerCase()) {
        case 'center':
            return {
                textAlign: 'text-center',
                buttonAlign: 'justify-center',
                containerAlign: 'mx-auto',
            };
        case 'right':
            return {
                textAlign: 'text-right',
                buttonAlign: 'justify-end',
                containerAlign: 'ml-auto',
            };
        case 'left':
        default:
            return {
                textAlign: 'text-left',
                buttonAlign: 'justify-start',
                containerAlign: '',
            };
    }
}

/* -------------------------------- Component -------------------------------- */

export default function ReviewPage(props) {
    // Content
    const Header = trim(valOf(props.Header, ''));
    const SubHeader = trim(valOf(props.SubHeader, ''));
    const HeaderSize = valOf(props.HeaderSize, 'large');
    const Intro = trim(valOf(props.Intro, ''));

    // Display
    const ColorScheme = valOf(props.ColorScheme, 'light');
    const VerticalSpacing = valOf(props.VerticalSpacing, 'medium');

    // Alignment (reviews usually centered)
    const Alignment = valOf(props.Alignment, 'center'); // 'left' | 'center' | 'right'
    const AlignmentAllowed = valOf(props.AlignmentAllowed, [
        'left',
        'center',
        'right',
    ]);

    // Animation + group
    const Animation = normalizeAnimation(valOf(props.Animation, 'fade'));
    const Duration = Number(valOf(props.Duration, 0.6)) || 0.6;
    const EditorPreview = Boolean(valOf(props.EditorPreview, false));
    const requestedGroup = mapGroup(
        valOf(props.AnimationGroup, 'text-content')
    );

    // CTAs (optional under the intro)
    const CTA = trim(valOf(props.CTA, ''));
    const CustomCTALink = trim(valOf(props.CustomCTALink, ''));
    const SecondaryButtonText = trim(valOf(props.SecondaryButtonText, ''));
    const SecondaryButtonLink = trim(valOf(props.SecondaryButtonLink, ''));
    const OutlineButtonText = trim(valOf(props.OutlineButtonText, ''));
    const OutlineButtonLink = trim(valOf(props.OutlineButtonLink, ''));
    const hasAnyCTA = Boolean(CTA || SecondaryButtonText || OutlineButtonText);

    const AnimationGroup =
        requestedGroup === 'ctas-only' && !hasAnyCTA
            ? 'text-content'
            : requestedGroup;

    const animateText = AnimationGroup === 'text-content';
    const animateCTAs = AnimationGroup === 'ctas-only';

    // Colors
    const {
        backgroundColor,
        textColor,
        headerColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    // Reduced motion
    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    // Animation props
    const preset = Animation;
    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const baseKey = `review-hero-${AnimationGroup}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, Duration, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};
        const v = isStagger
            ? getElementVariants('fade', Duration)
            : getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, preset, isStagger, Duration, EditorPreview]);

    // spacing + alignment (use shared hero spacing and header offset)
    const heroSpace = getHeroVerticalSpacing(VerticalSpacing);
    const { textAlign, buttonAlign, containerAlign } =
        getAlignmentClasses(Alignment);
    const headerSizeClass = getHeroHeaderSizeClasses(HeaderSize, 'page');
    const subHeaderSizeClass = getHeroHeaderSizeClasses('large', 'page');

    // Centralized CTA block (consistent with other sections)
    const { hasAny, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign,
            enableAnim: enableAnim && animateCTAs,
            motionProps: childMotionProps,
        }
    );

    {
        hasAny && CTAs; // keep for lint/treeshake parity
    }

    return (
        <div
            id="top"
            className="editable-component relative -mt-28 lg:-mt-48"
            data-alignment-allowed={
                Array.isArray(AlignmentAllowed)
                    ? AlignmentAllowed.join(',')
                    : ''
            }
        >
            <div
                className={`${backgroundColor} ${heroSpace.className}`}
                style={{ ...backgroundStyle, ...heroSpace.style }}
            >
                <div className="relative z-40 mx-auto w-full max-w-2xl px-6 lg:max-w-7xl lg:px-8 lg:py-32">
                    <div className={`${containerAlign} max-w-3xl ${textAlign}`}>
                        {/* Text block */}
                        <MotionMaybe
                            enable={enableAnim && isStagger && animateText}
                            k={`${baseKey}-container`}
                            motionProps={containerMotionProps}
                        >
                            {Header && (
                                <MotionMaybe
                                    enable={enableAnim && animateText}
                                    k={`${baseKey}-header`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        variant="h1"
                                        className={`font-display tracking-tight ${headerSizeClass} ${headerColor}`}
                                    >
                                        {Header}
                                    </MarkDown>
                                </MotionMaybe>
                            )}

                            {SubHeader && (
                                <MotionMaybe
                                    enable={enableAnim && animateText}
                                    k={`${baseKey}-subheader`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        variant={getChildHeadingVariant(
                                            Header,
                                            1
                                        )}
                                        className={`mt-2 font-display tracking-tight ${subHeaderSizeClass} ${headerColor}`}
                                    >
                                        {SubHeader}
                                    </MarkDown>
                                </MotionMaybe>
                            )}

                            {Intro && (
                                <MotionMaybe
                                    enable={enableAnim && animateText}
                                    k={`${baseKey}-intro`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        className={`mt-6 text-lg font-text leading-8 ${textColor}`}
                                    >
                                        {Intro}
                                    </MarkDown>
                                </MotionMaybe>
                            )}
                        </MotionMaybe>

                        {/* CTAs (optional) */}
                        {hasAnyCTA && (
                            <MotionMaybe
                                enable={enableAnim && animateCTAs}
                                k={`${baseKey}-ctas`}
                                motionProps={childMotionProps}
                                className={`mt-10 flex items-center ${buttonAlign} lg:mt-14`}
                            >
                                {CTAs}
                            </MotionMaybe>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
